/* global localStorage */

import React, { forwardRef, startTransition, useCallback, useEffect, useState } from 'react';
import SearchPageProductListItem from './SearchPageProductListItem';
import { array, bool, func, node, number, string } from 'prop-types';
import { CgMenuGridR } from 'react-icons/cg';
import { FaList } from 'react-icons/fa';
import PageNavButtons from './PageNavButtons';
import SearchWithin from './SearchWithin';
import NavigationSkeleton from './NavigationSkeleton';

const DUMMY_PRODUCTS_AMOUNT = 8;

const SearchPageProductList = forwardRef(({ isMobile, productList, searchLoading, collectionHandle, children, totalPages, curPage, paginateUser }, ref) => {
    const [view, setView] = useState('grid');

    const onViewChange = useCallback((view) => {
        setView(view);

        if (typeof window !== 'undefined') {
            localStorage.setItem('view', view);
        }
    }, []);

    useEffect(() => {
        if (typeof window === 'undefined') {
            return;
        }

        const view = localStorage.getItem('view');

        if (view) {
            startTransition(() => {
                setView(view);
            });
        }
    }, []);

    return (<ul
        ref={ref}
        className={'search-page--product-list'}>
        <div
            style={{
                display: 'flex',
                flexFlow: 'column wrap',
                gap: '1.25rem'
            }}>
            <div
                className={'search-page--header-container'}
            >
                {searchLoading ? (
                    <NavigationSkeleton />
                ) : null}
                {(!searchLoading && productList.length > 0) && (<>
                    {children?.props?.children[0]}
                    <div
                        style={{
                            display: 'flex',
                            gap: '10px',
                            justifyContent: 'end',
                            alignItems: 'center',
                        }}
                    >
                        {children?.props?.children[1]}
                        <button
                            className={`search-page-icon-wrapper ${view === 'grid' ? 'active-view' : ''}`}
                            onClick={() => onViewChange('grid')}
                            style={{ padding: '.25em' }}
                        >
                            <CgMenuGridR
                                fontSize={'32px'}
                            />
                        </button>
                        <button
                            className={`search-page-icon-wrapper ${view === 'list' ? 'active-view' : ''}`}
                            onClick={() => onViewChange('list')}
                        >
                            <FaList
                                fontSize={'27px'}
                            />
                        </button>
                    </div>
                </>)}
            </div>
            {!searchLoading && productList.length > 0 ? (<div
                style={{
                    alignItems: 'center',
                    display: 'flex',
                    flexFlow: 'row nowrap',
                    justifyContent: 'space-between',
                }}>
                <SearchWithin />
                <PageNavButtons
                    curPage={curPage}
                    totalPages={totalPages}
                    paginateUser={paginateUser}
                />
            </div>) : null}
        </div>
        {(searchLoading === false && !productList.length) && (<p
            style={{ height: '24px' }}>No products match your search criteria, please try again.</p>)}
        <div
            className={`search-page--product-list-inner ${view}`}>
            {(productList.length > 0 && searchLoading === false) && productList.map((product, i) => (
                <SearchPageProductListItem
                    index={i}
                    key={product.id}
                    isMobile={isMobile}
                    title={product.title}
                    handle={product.handle}
                    imageUrl={product.imageUrl}
                    price={product.price}
                    compareAtPrice={product.compareAtPrice}
                    availableForSale={product.availableForSale}
                    avgRating={product.avgRating}
                    totalReviews={product.totalReviews}
                    searchLoading={searchLoading}
                    collectionHandle={collectionHandle}
                    isFeatured={product.isFeatured}
                    inventoryLevelId={product?.availableInventoryLevels?.at(0)?.id ?? 0}
                    availableQuantity={product?.availableInventoryLevels?.at(0)?.availableQuantity ?? 0}
                    bypassNegativeQuantity={product?.availableInventoryLevels?.at(0)?.bypassNegativeQuantity ?? false}
                    view={view}
                    images={product.Images}
                />
            ))}
            {searchLoading === true && new Array(DUMMY_PRODUCTS_AMOUNT).fill(0).map((item, idx) => (
                <SearchPageProductListItem
                    index={idx}
                    key={`${item}_${idx}`}
                    isMobile={isMobile}
                    searchLoading={searchLoading}
                    view={view}
                />
            ))}
        </div>
        {!searchLoading && productList.length > 0 ? (<div
            style={{
                display: 'flex',
                justifyContent: 'flex-end',
            }}>
            <PageNavButtons
                curPage={curPage}
                totalPages={totalPages}
                paginateUser={paginateUser}
            />
        </div>) : null}
    </ul>);
});

SearchPageProductList.displayName = 'SearchPageProductList';

SearchPageProductList.propTypes = {
    isMobile: bool,
    productList: array,
    searchLoading: bool,
    collectionHandle: string,
    children: node,
    totalPages: number,
    curPage: number,
    paginateUser: func,
};

SearchPageProductList.defaultProps = {
    collectionHandle: '',
};

export default SearchPageProductList;